/**
 * PlayerDetailPage - Complete player prop view
 * Shows ALL props as a flat list with category headers (not accordions)
 * 
 * SSOT Two-Pipe Architecture:
 * - PIPE 1: useMasterStats for player stats (24hr cache)
 * - PIPE 2: Live lines passed from parent or useLiveOdds
 */
import React, { useState, useCallback, useMemo, useRef, memo, useEffect } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  ArrowLeft, Target, Zap, Crosshair, Plus, Volume2, Snowflake, Scale, Shield
} from 'lucide-react';
import { DemonIcon, GoblinIcon } from './Icons';
import { STAT_CATEGORIES, getCategoryKey, getTeamLogo, BACKEND_URL } from './constants';
import { BadgeRow, BADGE_REGISTRY, BadgeGridItem } from '../ui/BadgePill';
import GameLogBarChart from './GameLogBarChart';
import { MomentumTrackerFull } from './MomentumTracker';
import { MLBMatchupAnalysis } from './MLBMatchupAnalysis';
import { MarketGapDetail } from './MarketGapBadge';

// Gold Whistle Icon for High Whistle refs
const GoldWhistleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" fill="#FFD700" stroke="#FFD700"/>
    <path d="M12 9V4M12 4L9 7M12 4L15 7" stroke="#FFD700"/>
    <ellipse cx="12" cy="15" rx="5" ry="3" stroke="#FFD700"/>
  </svg>
);

// API URL for fetching player data
const API = BACKEND_URL || process.env.REACT_APP_BACKEND_URL || '';

// SSOT Global State Hooks
import { useMasterStats } from '../../hooks/useMasterStats';
import { useSport } from '../../context/SportContext';
import { normalizeFerrariPicks } from '../../utils/normalizeFerrariPick';

// ==================== PROP CATEGORY CONFIG ====================
// 2026-05-08 — universal stat-label adapter. PROP_LABELS / normalizeStatType /
// getPropLabel previously lived here as a sport-specific table. They've been
// migrated to `utils/statLabel.js::getStatLongLabel`. The CATEGORY_ORDER
// list below is kept as the canonical sort order for the detail page;
// every entry MUST be a value `getStatLabel(...)` can produce.
import { getStatLabel, getStatLongLabel } from '../../utils/statLabel';

const getPropLabel = getStatLongLabel;
const normalizeStatType = getStatLabel;

const CATEGORY_ORDER = [
  // NBA
  'PTS', 'REB', 'AST', 'PRA', 'P+R', 'P+A', 'R+A', '3PM', 'STL', 'BLK', 'BLST', 'TO', 'FGM', 'FTM', 'MIN',
  // MLB Batter
  'Hits', 'Total Bases', 'Singles', 'Doubles', 'Triples', 'RBIs', 'Runs', 'SB', 'HR',
  'Walks', 'Ks', 'H+R+RBI',
  // MLB Pitcher
  'Outs', 'Hits Allowed', 'ER', 'BB Allowed',
  // Team-prop categories (TeamDetailPage reuses PlayerDetailPage 1:1)
  'Team Total', 'Game Total', 'Spread', 'Moneyline', 'Opp Total',
];

// ==================== PLAYER HEADSHOT ====================
const PlayerHeadshot = memo(({ playerName, team, photoUrl, sport, teamLogoUrl: explicitLogoUrl, size = 'md', className = '' }) => {
  const [error, setError] = useState(false);
  const sizeClasses = { sm: 'w-8 h-8', md: 'w-12 h-12', lg: 'w-16 h-16', xl: 'w-24 h-24' };
  const sizeClass = sizeClasses[size] || sizeClasses.md;
  
  const isValidPhotoUrl = photoUrl && !photoUrl.includes('nophoto');
  // Sport-aware logo resolution. Backend `team_logo_url` wins if set,
  // else lookup against the sport-keyed map. NEVER cross-populates.
  const teamLogoUrl = getTeamLogo(sport, team, explicitLogoUrl);
  
  if (!isValidPhotoUrl || error) {
    if (teamLogoUrl) {
      return (
        <div className={`${sizeClass} rounded-full overflow-hidden bg-zinc-800 flex items-center justify-center p-1.5 ${className}`}>
          <img src={teamLogoUrl} alt={team} className="w-full h-full object-contain" onError={(e) => e.target.style.display = 'none'} />
        </div>
      );
    }
    return (
      <div className={`${sizeClass} rounded-full bg-zinc-800 flex items-center justify-center text-zinc-500 font-bold ${className}`}>
        {playerName?.charAt(0) || '?'}
      </div>
    );
  }
  
  return (
    <div className={`${sizeClass} rounded-full overflow-hidden bg-zinc-800 ${className}`}>
      <img src={photoUrl} alt={playerName} onError={() => setError(true)} 
        className="w-full h-full object-cover" style={{ objectPosition: 'center 20%', transform: 'scale(1.3)' }} />
    </div>
  );
});

// ==================== SKELETON LOADER ====================
const SkeletonPlayerDetail = () => (
  <div className="space-y-4 animate-pulse">
    {[1, 2, 3, 4, 5].map(i => (
      <div key={i} className="bg-zinc-800/50 rounded-lg h-16" />
    ))}
  </div>
);

// ==================== SINGLE PROP ROW ====================
const PropRow = memo(({ prop, isHighlighted, highlightRef, onVisionClick, gameLogs = [], baselineStats = {} }) => {
  // NEW: Sharp movement classification
  const hasSharpMovement = prop.sharp_movement;
  const tierLabel = prop.tier_label || 'STANDARD';
  
  // LEGACY FALLBACK for backward compatibility
  const isDemon = prop.is_demon || tierLabel === 'DEMON' || tierLabel === 'WAR_ZONE';
  const isGoblin = prop.is_goblin || tierLabel === 'GOBLIN' || tierLabel === 'SAFE_HAVEN';
  
  const line = prop.line || 0;
  const direction = (prop.direction || 'over').toUpperCase();
  const statType = prop.stat_type || prop.market || '';
  
  // Whistle Matrix data
  const crewChief = prop.crew_chief;
  const refOuPct = prop.ref_ou_pct;
  const refPpg = prop.ref_ppg;
  const whistleClass = prop.whistle_class;
  const hasWhistleModifier = prop.has_whistle_modifier;
  const whistleModifier = prop.whistle_modifier;
  
  // Point Lift (Vegas Intel)
  const pointLift = prop.point_lift;
  const liftLabel = prop.lift_label;
  const liftType = prop.lift_type;
  const foulRateDiff = prop.foul_rate_diff;
  
  // Usage Vacuum data
  const hasVacuumModifier = prop.has_vacuum_modifier;
  const vacuumModifier = prop.vacuum_modifier;
  const vacuumData = prop.vacuum_data;
  
  // Stats from baseline. Backend ships per-prop fields ONLY on the
  // `/api/v3/ferrari/*` dashboard payloads — the `/api/v3/player-
  // with-badges/{name}` payload that drives this page does NOT carry
  // `prop.l5_avg / l10_avg / season_avg`. The secondary master-hub
  // fetch populates `player.baseline_stats[stat_type] = { season_avg,
  // l5_avg, l10_avg, l20_avg, l10_values }`, so we resolve the
  // per-stat baseline from that table when the per-prop field is
  // absent. Without this fallback the L5 AVG / L10 AVG / SEASON
  // cells (PropRow + Vision Intel Suite header) render `-` for every
  // NBA prop.
  const _baselineForStat =
    baselineStats?.[prop.stat_type_extracted] ||
    baselineStats?.[prop.stat_type] ||
    {};
  const l5Avg     = prop.l5_avg     ?? _baselineForStat.l5_avg     ?? null;
  const l10Avg    = prop.l10_avg    ?? _baselineForStat.l10_avg    ?? null;
  const seasonAvg = prop.season_avg ?? _baselineForStat.season_avg ?? null;

  // Hit rates (percentage) - Handle both decimal (0-1) and percentage (0-100) formats
  const normalizeHitRate = (rate) => {
    if (rate == null) return 0;
    // If rate is > 1 and <= 100, it's already a percentage
    if (rate > 1 && rate <= 100) return Math.round(rate);
    // If rate is > 100, it's been double-converted, divide by 100
    if (rate > 100) return Math.round(rate / 100);
    // If rate is <= 1, it's a decimal, multiply by 100
    return Math.round(rate * 100);
  };

  // 2026-05-07 P0 Phase 4B: canonical-only L5/L10 reads. Legacy
  // `prop.h10_rate`/`prop.h5_rate`/`prop.hit_rates` and
  // `prop.l10_hit_rate`/`prop.l5_hit_rate` aliases are no longer
  // shipped on tier picks; the L5/L10 cells consume the score-doc
  // SSOT fields directly.
  const h10Rate = prop.hit_rate_l10 != null ? normalizeHitRate(prop.hit_rate_l10) : 0;
  const h5Rate  = prop.hit_rate_l5  != null ? normalizeHitRate(prop.hit_rate_l5)  : 0;
  
  const getHitRateColor = (rate) => {
    if (rate >= 80) return 'text-green-400';
    if (rate >= 60) return 'text-yellow-400';
    if (rate >= 40) return 'text-orange-400';
    return 'text-red-400';
  };
  
  // Handle click for Vision Pick - ONLY highlighted prop should be clickable
  const handleClick = () => {
    if (isHighlighted && onVisionClick) {
      onVisionClick(prop);
    }
  };
  
  return (
    <div 
      ref={isHighlighted ? highlightRef : null}
      onClick={isHighlighted ? handleClick : undefined}
      className={`flex flex-col p-4 rounded-lg transition-all ${
        isHighlighted
          ? 'bg-gradient-to-r from-amber-950/50 via-yellow-900/30 to-amber-950/50 border-2 border-amber-400 ring-2 ring-amber-400/50 cursor-pointer hover:ring-amber-300/70' 
          : isDemon 
            ? 'bg-gradient-to-r from-red-950/40 to-zinc-900 border border-red-500/30' 
            : isGoblin 
              ? 'bg-gradient-to-r from-green-950/40 to-zinc-900 border border-green-500/30'
              : 'bg-zinc-800/30 border border-zinc-700/30'
      }`}
      style={isHighlighted ? { boxShadow: '0 0 25px rgba(251, 191, 36, 0.5), 0 0 50px rgba(251, 191, 36, 0.3), inset 0 0 20px rgba(251, 191, 36, 0.1)' } : {}}
      data-testid={`prop-row-${prop.stat_type}-${line}${isHighlighted ? '-vision' : ''}`}
    >
      {/* TOP ROW: Icon + Line + Badges */}
      <div className="flex items-center gap-2 mb-3">
        {isHighlighted ? (
          <Crosshair className="w-5 h-5 text-amber-400 animate-pulse" />
        ) : isDemon ? (
          <DemonIcon size={18} />
        ) : isGoblin ? (
          <GoblinIcon size={18} />
        ) : (
          <Target className="w-4 h-4 text-zinc-500" />
        )}
        <span className={`text-lg font-bold ${
          isHighlighted ? 'text-amber-300' : isDemon ? 'text-red-400' : isGoblin ? 'text-green-400' : 'text-white'
        }`}>
          {direction} {line}
        </span>
        {isHighlighted && (
          <>
            <span className="px-1.5 py-0.5 text-[8px] font-black bg-gradient-to-r from-amber-500 to-yellow-400 text-black rounded-full flex items-center gap-0.5 animate-pulse">
              <Crosshair className="w-2.5 h-2.5" />
              VISION
            </span>
            <span className="text-[9px] text-cyan-400 font-medium animate-pulse ml-auto">
              Click for Intel Suite
            </span>
          </>
        )}
        {/* Vegas Intel - Officiating Impact Badge */}
        {crewChief && hasWhistleModifier && (
          <span className={`px-1.5 py-0.5 text-[8px] font-semibold rounded-full flex items-center gap-1 ${
            whistleClass === 'high_whistle' 
              ? 'bg-gradient-to-r from-amber-500/20 to-yellow-500/10 text-amber-400 border border-amber-500/30' 
              : whistleClass === 'low_whistle'
                ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/10 text-blue-400 border border-blue-500/30'
                : 'bg-zinc-700/50 text-zinc-400'
          }`} data-testid="vegas-intel-badge">
            {whistleClass === 'high_whistle' ? (
              <GoldWhistleIcon className="w-3 h-3" />
            ) : (
              <Snowflake className="w-3 h-3" />
            )}
            <span>{liftLabel || `${pointLift > 0 ? '+' : ''}${pointLift} PTS`}</span>
          </span>
        )}
        {/* Usage Vacuum Badge */}
        {hasVacuumModifier && vacuumData && (
          <span className="px-1.5 py-0.5 text-[8px] font-semibold rounded-full flex items-center gap-1 bg-gradient-to-r from-orange-500/20 to-red-500/10 text-orange-400 border border-orange-500/30" data-testid="vacuum-badge">
            <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
            <span>+{vacuumModifier} ({vacuumData.injured_player?.split(' ').pop()} OUT)</span>
          </span>
        )}
      </div>
      
      {/* LASSO PROJECTION BAR + VISION INTEL + SCOUT BADGES */}
      {(prop.vk_predicted || prop.vision_intel || prop.intel_suite?.lasso) && (() => {
        const lasso = prop.intel_suite?.lasso || {};
        const vkPred = prop.vk_predicted || lasso.projection;
        // SSOT Tier E (2026-05-04): canonical `edge_vs_fair` is a
        // ratio; convert to raw units via × line. The previous
        // `vk_edge || (vkPred - line)` fallback chain was the last
        // frontend reader of `vk_edge` and has been removed.
        const vkEdge = (prop.edge_vs_fair != null && line != null)
          ? Number(prop.edge_vs_fair) * Number(line)
          : (vkPred != null && line != null ? vkPred - line : null);
        const edgePct = vkEdge && line ? ((vkEdge / line) * 100) : 0;
        const confidenceTier = prop.lasso_confidence || lasso.confidence_tier;
        const visionText = prop.vision_intel || prop.vision_summary;
        const scoutBadges = prop.scout_badges || prop.intel_suite?.scout_badges || [];
        const isHighEdge = Math.abs(edgePct) >= 15;
        const edgePositive = vkEdge > 0;

        return (
          <div className="mt-2 space-y-2">
            {/* Lasso Projection vs Line */}
            {vkPred != null && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${
                isHighEdge && edgePositive ? 'bg-green-950/40 border border-green-500/30' :
                isHighEdge && !edgePositive ? 'bg-red-950/40 border border-red-500/30' :
                'bg-zinc-800/40 border border-zinc-700/30'
              }`} data-testid="lasso-projection-bar">
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] uppercase tracking-wide text-zinc-500">Proj</span>
                  <span className={`font-bold text-sm ${edgePositive ? 'text-green-400' : 'text-red-400'}`}>
                    {typeof vkPred === 'number' ? vkPred.toFixed(1) : vkPred}
                  </span>
                </div>
                {vkEdge != null && (
                  <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    isHighEdge && edgePositive ? 'bg-green-500/20 text-green-400' :
                    isHighEdge && !edgePositive ? 'bg-red-500/20 text-red-400' :
                    edgePositive ? 'text-green-400/70' : 'text-red-400/70'
                  }`}>
                    {edgePositive ? '+' : ''}{typeof vkEdge === 'number' ? vkEdge.toFixed(1) : vkEdge}
                    <span className="text-[8px] opacity-70">({edgePct >= 0 ? '+' : ''}{edgePct.toFixed(0)}%)</span>
                  </div>
                )}
                {confidenceTier && (
                  <span className={`ml-auto px-1.5 py-0.5 rounded text-[8px] font-bold tracking-wide ${
                    confidenceTier === 'HIGH_FIDELITY' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                    confidenceTier === 'MODERATE' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                    'bg-zinc-700/50 text-zinc-400'
                  }`} data-testid="confidence-tier-badge">
                    {confidenceTier === 'HIGH_FIDELITY' ? 'HI-FI' : confidenceTier === 'MODERATE' ? 'MOD' : 'HI-VAR'}
                  </span>
                )}
              </div>
            )}

            {/* Scout Badges + Vision Intel moved to Intel Suite modal */}
          </div>
        );
      })()}

      {/* UNIVERSAL EDGE METRICS — Consensus Edge / Best Bet Edge / Best Book.
          Sport-agnostic. Renders only when at least one canonical field is
          shipped on the prop. Mirrors the in-modal block (line ~1693) so
          users see the same edge basis on the pick listing AND inside the
          Vision Intel Suite without opening the modal. (2026-05-15) */}
      {(() => {
        const consensusPct = prop.edge_vs_fair != null
          ? Number(prop.edge_vs_fair) * 100
          : null;
        const bestBetPct = prop.total_edge != null
          ? Number(prop.total_edge) * 100
          : null;
        const bestBook = prop.best_book;
        const bestOdds = prop.best_book_odds;
        const isRawOneSided = prop.best_bet_edge_source === 'raw_one_sided';
        if (consensusPct == null && bestBetPct == null && !bestBook) return null;
        const bookMap = {
          'draftkings': 'DraftKings', 'fanduel': 'FanDuel',
          'betmgm': 'BetMGM', 'caesars': 'Caesars',
          'espnbet': 'ESPN BET', 'hardrockbet': 'Hard Rock',
          'betrivers': 'BetRivers', 'betparx': 'BetPARX',
          'ballybet': 'Bally Bet', 'fliff': 'Fliff',
          'pointsbetus': 'PointsBet', 'betonline': 'BetOnline',
        };
        const bookLabel = bestBook ? (bookMap[bestBook] || bestBook) : null;
        const fmtOdds = (o) => o == null ? '' : (o > 0 ? `+${o}` : `${o}`);
        const consensusColor = consensusPct == null ? 'text-zinc-400'
          : consensusPct > 15 ? 'text-green-400'
          : consensusPct > 5 ? 'text-yellow-400'
          : consensusPct < -5 ? 'text-red-400'
          : 'text-zinc-300';
        const bestBetColor = bestBetPct == null ? 'text-zinc-400'
          : bestBetPct > 10 ? 'text-emerald-400'
          : bestBetPct > 3 ? 'text-amber-300'
          : bestBetPct < 0 ? 'text-red-400'
          : 'text-zinc-300';
        return (
          <div
            className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5 rounded-md bg-zinc-900/60 border border-zinc-800/80 text-[10.5px] font-mono"
            data-testid={`prop-row-edge-metrics-${prop.stat_type}-${line}`}
          >
            {consensusPct != null && (
              <div
                className="flex items-baseline gap-1.5"
                data-testid={`prop-row-consensus-edge-${prop.stat_type}-${line}`}
                title="Consensus Edge: model probability vs market devigged fair probability."
              >
                <span className="text-zinc-500 uppercase tracking-[0.12em]">Consensus</span>
                <span className={`${consensusColor} font-bold tabular-nums`}>
                  {consensusPct > 0 ? '+' : ''}{consensusPct.toFixed(1)}%
                </span>
              </div>
            )}
            {bestBetPct != null && (
              <div
                className="flex items-baseline gap-1.5"
                data-testid={`prop-row-best-bet-edge-${prop.stat_type}-${line}`}
                title={isRawOneSided
                  ? "Best Bet Edge (raw): best book lacks opposite side — vig not removable, basis-mismatched vs Consensus."
                  : "Best Bet Edge: model vs best available devigged sportsbook price."}
              >
                <span className="text-zinc-500 uppercase tracking-[0.12em]">Best Bet</span>
                <span className={`${bestBetColor} font-bold tabular-nums`}>
                  {bestBetPct > 0 ? '+' : ''}{bestBetPct.toFixed(1)}%
                  {isRawOneSided && <span className="text-zinc-500 ml-0.5">*</span>}
                </span>
              </div>
            )}
            {bookLabel && (
              <div
                className="flex items-baseline gap-1.5 ml-auto"
                data-testid={`prop-row-best-book-${prop.stat_type}-${line}`}
                title="Best available sportsbook price for this side."
              >
                <span className="text-zinc-500 uppercase tracking-[0.12em]">Book</span>
                <span className="text-emerald-300 font-semibold">{bookLabel}</span>
                {bestOdds != null && (
                  <span className="text-zinc-300 tabular-nums">{fmtOdds(bestOdds)}</span>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* MARKET GAP — sport-agnostic book-disagreement signal.
          Silent unless prop crosses medium threshold. */}
      <MarketGapDetail pick={prop} />

      {/* BOTTOM ROW: Stats column on left, Chart on right - Stack on mobile */}
      <div className="flex flex-col sm:flex-row items-stretch gap-4">
        {/* LEFT: Stats Column - Full width on mobile */}
        <div className={`flex flex-row sm:flex-col justify-around sm:justify-center gap-2 py-2 px-3 rounded-md ${
          isHighlighted ? 'bg-amber-900/30' : 'bg-zinc-800/50'
        }`} style={{ minWidth: '90px' }}>
          <div className="text-center">
            <div className={`text-[9px] uppercase tracking-wide ${isHighlighted ? 'text-amber-400/70' : 'text-zinc-500'}`}>L10 Hit</div>
            <div className={`font-bold text-sm ${isHighlighted ? 'text-amber-300' : getHitRateColor(h10Rate)}`}>
              {h10Rate > 0 ? `${h10Rate}%` : '-'}
            </div>
          </div>
          <div className="text-center">
            <div className={`text-[9px] uppercase tracking-wide ${isHighlighted ? 'text-amber-400/70' : 'text-zinc-500'}`}>L5 Hit</div>
            <div className={`font-bold text-sm ${isHighlighted ? 'text-amber-300' : getHitRateColor(h5Rate)}`}>
              {h5Rate > 0 ? `${h5Rate}%` : '-'}
            </div>
          </div>
          <div className="hidden sm:block w-full h-px bg-zinc-700 my-1" />
          <div className="text-center">
            <div className={`text-[9px] uppercase tracking-wide ${isHighlighted ? 'text-amber-400/70' : 'text-zinc-500'}`}>L5 Avg</div>
            <div className={`font-bold text-sm ${isHighlighted ? 'text-amber-300' : 'text-white'}`}>
              {l5Avg != null ? l5Avg : '-'}
            </div>
          </div>
          <div className="text-center">
            <div className={`text-[9px] uppercase tracking-wide ${isHighlighted ? 'text-amber-400/70' : 'text-zinc-500'}`}>L10 Avg</div>
            <div className={`font-bold text-sm ${isHighlighted ? 'text-amber-300' : 'text-white'}`}>
              {l10Avg != null ? l10Avg : '-'}
            </div>
          </div>
          <div className="text-center">
            <div className={`text-[9px] uppercase tracking-wide ${isHighlighted ? 'text-amber-400/70' : 'text-zinc-500'}`}>Season</div>
            <div className={`font-bold text-sm ${isHighlighted ? 'text-amber-300' : 'text-white'}`}>
              {seasonAvg != null ? seasonAvg : '-'}
            </div>
          </div>
        </div>
        
        {/* RIGHT: Bar Chart - Full width on both mobile and desktop */}
        {gameLogs && gameLogs.length > 0 && (
          <div className="flex-1 flex flex-col w-full">
            <GameLogBarChart
              gameLogs={gameLogs}
              statType={statType}
              line={line}
              direction={prop?.direction || prop?.recommendation || 'OVER'}
              showGames={10}
              height="100%"
              l5Avg={l5Avg}
              l10Avg={l10Avg}
              seasonAvg={seasonAvg}
              hitProfile={prop && (prop.l10_hit_count != null) ? {
                l10_hit_count: prop.l10_hit_count,
                l10_total: prop.l10_total,
                l10_values: prop.l10_values,
                hit_profile_line: prop.hit_profile_line,
                hit_profile_rule: prop.hit_profile_rule,
              } : null}
              className="flex-1 w-full"
            />
          </div>
        )}
      </div>
    </div>
  );
});

// ==================== CATEGORY HEADER ====================
const CategoryHeader = memo(({ category, count, hasDemon, hasGoblin }) => {
  const label = getPropLabel(category);
  
  return (
    <div className="flex items-center gap-2 py-2 px-1 border-b border-zinc-700/50 mt-4 first:mt-0">
      <span className="text-sm font-bold uppercase tracking-wider text-zinc-300">
        {label}
      </span>
      <span className="text-[10px] text-zinc-500">
        ({count} line{count > 1 ? 's' : ''})
      </span>
      {hasDemon && (
        <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-500/20">
          <DemonIcon size={10} />
        </div>
      )}
      {hasGoblin && (
        <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-500/20">
          <GoblinIcon size={10} />
        </div>
      )}
    </div>
  );
});

// ==================== MAIN COMPONENT ====================
// SSOT: Uses useMasterStats hook for player data (PIPE 1)
export const PlayerDetailPage = ({ playerName, playerData = null, onBack, highlightProp = null, highlightType = 'demon', onQuickAdd = null }) => {
  // Get current sport from context
  const { currentSport } = useSport();
  
  // Direct state-based fetch
  const [player, setPlayer] = useState(playerData);
  const [loading, setLoading] = useState(!playerData);
  const [error, setError] = useState(null);
  const fetchIdRef = useRef(0);
  
  // Store original clicked prop's intel_suite for fallback
  const originalClickedProp = useRef(playerData?.props?.[0] || null);
  
  useEffect(() => {
    // If we have partial playerData (single prop), still fetch all props
    const hasOnlyOneProp = playerData?.props?.length === 1;
    
    if (playerData && !hasOnlyOneProp) {
      setPlayer(playerData);
      setLoading(false);
      return;
    }
    
    if (!playerName) return;
    
    // Increment fetch ID to track current request
    const currentFetchId = ++fetchIdRef.current;
    
    // Use sport-specific endpoint for MLB, generic for NBA
    const fetchUrl = currentSport === 'mlb' 
      ? `${API}/api/v3/mlb/player/${encodeURIComponent(playerName)}`
      : `${API}/api/v3/player-with-badges/${encodeURIComponent(playerName)}`;
    
    setLoading(true);
    setError(null);
    
    // Use XMLHttpRequest with onreadystatechange for better compatibility
    const xhr = new XMLHttpRequest();
    xhr.open('GET', fetchUrl, true);
    xhr.setRequestHeader('Accept', 'application/json');
    
    xhr.onreadystatechange = function() {
      if (xhr.readyState !== 4) return;
      
      // Always process, don't skip based on fetchId
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          
          if (data.success && data.player) {
            // Normalize props from /player-with-badges — the endpoint returns
            // raw cached-board shape (stat_type_extracted=None, market=None,
            // direction=None, chart_data=None). Apply the same adapter used
            // by the Ferrari tier hooks so the detail list grouping, bar
            // charts, and direction filtering all work.
            const normalizedProps = Array.isArray(data.player.props)
              ? normalizeFerrariPicks(data.player.props)
              : [];

            // Merge with any passed playerData (to preserve goblin/demon flags)
            const mergedPlayer = {
              ...data.player,
              name: data.player.name || playerName,
              player_name: playerName,
              props: normalizedProps,
            };
            
            // If we had partial playerData with flags, merge those flags into matching props
            if (hasOnlyOneProp && playerData?.props?.[0]) {
              const clickedProp = playerData.props[0];
              mergedPlayer.props = mergedPlayer.props?.map(prop => {
                // Check if this prop matches the clicked one
                // Use flexible matching: stat_type or normalized versions
                const propStatType = (prop.stat_type || '').toLowerCase().replace(/[^a-z]/g, '');
                const clickedStatType = (clickedProp.stat_type || '').toLowerCase().replace(/[^a-z]/g, '');
                const statMatch = propStatType === clickedStatType || 
                                  prop.stat_type === clickedProp.stat_type;
                
                if (statMatch && Math.abs((prop.line || 0) - (clickedProp.line || 0)) < 0.01) {
                  return {
                    ...prop,
                    // Core tier fields
                    is_goblin: clickedProp.is_goblin || prop.is_goblin,
                    is_demon: clickedProp.is_demon || prop.is_demon,
                    tier: clickedProp.tier || prop.tier,
                    tier_label: clickedProp.tier_label || prop.tier_label,
                    dk_tier: clickedProp.dk_tier || prop.dk_tier,
                    ferrari_tier: clickedProp.ferrari_tier || prop.ferrari_tier,
                    // Hit rate fields
                    // 2026-05-07 P0 Phase 4B: canonical-only carry.
                    // Legacy `h5_rate` / `h10_rate` / `h20_rate` no
                    // longer present on tier picks; the trio below
                    // is sufficient for the L5/L10/L20 chips and
                    // the side-aware over/under tiles.
                    hit_rate_l5: clickedProp.hit_rate_l5 ?? prop.hit_rate_l5,
                    hit_rate_l10: clickedProp.hit_rate_l10 ?? prop.hit_rate_l10,
                    hit_rate_l20: clickedProp.hit_rate_l20 ?? prop.hit_rate_l20,
                    hit_rate_over: clickedProp.hit_rate_over ?? prop.hit_rate_over,
                    hit_rate_under: clickedProp.hit_rate_under ?? prop.hit_rate_under,
                    // Average fields
                    l5_avg: clickedProp.l5_avg ?? prop.l5_avg,
                    l10_avg: clickedProp.l10_avg ?? prop.l10_avg,
                    l20_avg: clickedProp.l20_avg ?? prop.l20_avg,
                    season_avg: clickedProp.season_avg ?? prop.season_avg,
                    // Vision Intel fields - direct string fields
                    vision_intel: clickedProp.vision_intel || prop.vision_intel,
                    vision_badge: clickedProp.vision_badge || prop.vision_badge,
                    vision_summary: clickedProp.vision_summary || prop.vision_summary,
                    intel_score: clickedProp.intel_score ?? prop.intel_score,
                    intel_verdict: clickedProp.intel_verdict || prop.intel_verdict,
                    intel_risk: clickedProp.intel_risk || prop.intel_risk,
                    adjusted_confidence: clickedProp.adjusted_confidence ?? prop.adjusted_confidence,
                    // Oracle summary (MLB)
                    oracle_summary: clickedProp.oracle_summary || prop.oracle_summary,
                    // Badges — Ferrari tier payload is the current source of
                    // truth. `/player-with-badges` does not return
                    // momentum_data / scout_badges / active_badges, so the
                    // clicked-pick copies must win.
                    scout_badges: clickedProp.scout_badges || prop.scout_badges,
                    context_badges: clickedProp.context_badges || prop.context_badges,
                    active_badges: clickedProp.active_badges || prop.active_badges,
                    // Defensive Momentum (NBA) — current SoT is the root-level
                    // `momentum_data` emitted by Ferrari scoring. `intel_suite
                    // .defensive_momentum` is the legacy field and is no
                    // longer emitted by the backend; do NOT fall back to it.
                    momentum_data: clickedProp.momentum_data || prop.momentum_data,
                    has_momentum_modifier:
                      clickedProp.has_momentum_modifier ?? prop.has_momentum_modifier,
                    momentum_modifier:
                      clickedProp.momentum_modifier ?? prop.momentum_modifier,
                    // Officiating / vacuum modifier fields (also only present
                    // on the Ferrari payload).
                    crew_chief: clickedProp.crew_chief || prop.crew_chief,
                    whistle_class: clickedProp.whistle_class || prop.whistle_class,
                    whistle_modifier:
                      clickedProp.whistle_modifier ?? prop.whistle_modifier,
                    has_whistle_modifier:
                      clickedProp.has_whistle_modifier ?? prop.has_whistle_modifier,
                    point_lift: clickedProp.point_lift ?? prop.point_lift,
                    lift_label: clickedProp.lift_label || prop.lift_label,
                    lift_type: clickedProp.lift_type || prop.lift_type,
                    has_vacuum_modifier:
                      clickedProp.has_vacuum_modifier ?? prop.has_vacuum_modifier,
                    vacuum_modifier:
                      clickedProp.vacuum_modifier ?? prop.vacuum_modifier,
                    vacuum_data: clickedProp.vacuum_data || prop.vacuum_data,
                    // Opponent — prefer clicked pick's normalized opponent
                    opponent: clickedProp.opponent || prop.opponent,
                    opponent_abbr:
                      clickedProp.opponent_abbr || prop.opponent_abbr,
                    // MLB matchup analysis (split / pitcher)
                    matchup_analysis:
                      clickedProp.matchup_analysis || prop.matchup_analysis,
                    // INTEL SUITE - Critical for Tempo, Badges, etc.
                    // Merge both intel_suite objects to preserve all fields (lasso from tier, tempo/stability from player detail)
                    intel_suite: {
                      ...(prop.intel_suite || {}),
                      ...(clickedProp.intel_suite || {}),
                    },
                    // Edge and probability fields.
                    // SSOT Tier E (2026-05-04): `edge_vs_fair` is the
                    // canonical. Legacy aliases `edge_pct` and
                    // `vk_edge` are stamped by upstream for back-
                    // compat but no longer propagated through the
                    // click-through merge — components read
                    // `edge_vs_fair * line` directly.
                    edge: clickedProp.edge ?? prop.edge,
                    edge_vs_fair: clickedProp.edge_vs_fair ?? prop.edge_vs_fair,
                    cv: clickedProp.cv ?? prop.cv,
                    tp_odds: clickedProp.tp_odds ?? prop.tp_odds,
                    board_score: clickedProp.board_score ?? prop.board_score,
                    // VK prediction fields
                    vk_predicted: clickedProp.vk_predicted ?? prop.vk_predicted,
                    vk_prob_over: clickedProp.vk_prob_over ?? prop.vk_prob_over,
                    vk_prob_under: clickedProp.vk_prob_under ?? prop.vk_prob_under,
                    vk_recommendation: clickedProp.vk_recommendation || prop.vk_recommendation,
                    projected_value: clickedProp.projected_value ?? prop.projected_value,
                  };
                }
                return prop;
              }) || [];
            }
            
            setPlayer(mergedPlayer);

            // Secondary fetch: master-hub by bdl_player_id for game_logs +
            // baseline_stats. `/api/v3/player-with-badges` does not return
            // these and the detail view's bar chart + PPG/RPG/APG header
            // stats strip both depend on them.
            const bdlId =
              mergedPlayer.bdl_player_id ||
              mergedPlayer.bdl_id ||
              playerData?.props?.[0]?.bdl_player_id ||
              playerData?.props?.[0]?.bdl_id;
            if (bdlId && currentSport !== 'mlb') {
              fetch(`${API}/api/v3/master-hub/player/${bdlId}`)
                .then((r) => (r.ok ? r.json() : null))
                .then((hub) => {
                  if (!hub) return;
                  const hubPlayer = hub.player || hub;
                  // Prefer `bdl_game_logs` when it has richer usable
                  // opponent data than `game_logs`. The Tank01-shape
                  // `game_logs` array sometimes lacks `opponent_team_id`,
                  // `matchup`, and `home_game`, while the BDL-shape
                  // `bdl_game_logs` array carries `opponent_team_id` +
                  // `home_game` reliably.
                  const tank = Array.isArray(hubPlayer.game_logs)
                    ? hubPlayer.game_logs
                    : [];
                  const bdl = Array.isArray(hubPlayer.bdl_game_logs)
                    ? hubPlayer.bdl_game_logs
                    : [];
                  const tankFirst = tank[0] || {};
                  const tankHasOpponent =
                    tankFirst.opponent_team_id != null ||
                    (typeof tankFirst.matchup === 'string' && tankFirst.matchup.trim() !== '') ||
                    (typeof tankFirst.opp === 'string' && tankFirst.opp.trim() !== '');
                  const preferBdl =
                    bdl.length > 0 && (!tankHasOpponent || bdl.length > tank.length);
                  const gameLogs = preferBdl ? bdl : tank;
                  const baselineStats = hubPlayer.baseline_stats || {};
                  setPlayer((prev) => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      game_logs: gameLogs,
                      baseline_stats:
                        Object.keys(prev.baseline_stats || {}).length > 0
                          ? prev.baseline_stats
                          : baselineStats,
                    };
                  });
                })
                .catch(() => {
                  /* silent — master-hub is a secondary enrichment */
                });
            }
          } else {
            setError('No available Bets today');
          }
        } catch (e) {
          setError('Failed to parse response');
        }
      } else if (xhr.status > 0) {
        setError(`HTTP error: ${xhr.status}`);
      }
      setLoading(false);
    };
    
    xhr.onerror = function() {
      if (fetchIdRef.current !== currentFetchId) return;
      setError('Network error');
      setLoading(false);
    };
    
    xhr.send();
  }, [playerName, playerData, currentSport]);
  
  const [showIntelSuite, setShowIntelSuite] = useState(false);
  const [selectedVisionProp, setSelectedVisionProp] = useState(null);
  const highlightRef = useRef(null);
  
  // Handle Vision Pick click - merge intel_suite from original clicked prop if missing
  const handleVisionClick = useCallback((prop) => {
    // Ensure intel_suite is available - use original clicked prop's data if missing
    const enrichedProp = {
      ...prop,
      intel_suite: prop.intel_suite || originalClickedProp.current?.intel_suite,
      vision_intel: prop.vision_intel || originalClickedProp.current?.vision_intel,
      vision_badge: prop.vision_badge || originalClickedProp.current?.vision_badge,
    };
    setSelectedVisionProp(enrichedProp);
    setShowIntelSuite(true);
  }, []);
  
  // Parse highlight info (format: "stat_type|line|direction")
  const highlightInfo = useMemo(() => {
    if (!highlightProp) return null;
    const parts = highlightProp.split('|');
    if (parts.length >= 2) {
      return {
        statType: parts[0],
        line: parseFloat(parts[1]),
        direction: parts[2] || 'Over'
      };
    }
    return null;
  }, [highlightProp]);
  
  // Group props by normalized category - prioritize stat_type_extracted for specific categories
  const groupedProps = useMemo(() => {
    if (!player?.props) return {};
    
    const groups = {};
    player.props.forEach(prop => {
      // Use stat_type_extracted (AST, PTS, PRA, etc.) as primary, fallback to generic category
      const rawCat = prop.stat_type_extracted || getCategoryKey(prop.market) || 'OTHER';
      const cat = normalizeStatType(rawCat);
      if (!groups[cat]) groups[cat] = [];
      // 2026-05-13 SSOT — DO NOT overwrite `prop.stat_type` with the
      // display label here. The canonical stat_type ('PR'/'PA'/'RA'/
      // 'PRA') is the SSOT token that GameLogBarChart's STAT_FIELD_MAP
      // keys on. Overwriting it to 'P+R' (display label) caused the
      // chart to fail to resolve game-log values and render "No game
      // data" for every combo prop (Ayo Dosunmu PR 19.5 War Zone bug).
      // Store the display label as `stat_display_label` so the section
      // header / sorting still has the formatted token to render.
      groups[cat].push({ ...prop, stat_display_label: cat });
    });
    
    return groups;
  }, [player]);
  
  // Sort categories
  const sortedCategories = useMemo(() => {
    const keys = Object.keys(groupedProps);
    return keys.sort((a, b) => {
      const aIdx = CATEGORY_ORDER.indexOf(a);
      const bIdx = CATEGORY_ORDER.indexOf(b);
      if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });
  }, [groupedProps]);
  
  // Auto-scroll to highlighted prop
  useEffect(() => {
    if (highlightInfo && highlightRef.current) {
      setTimeout(() => {
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [highlightInfo, player]);
  
  // Check if prop is highlighted (Vision Pick)
  // A prop is highlighted if:
  // 1. It matches the highlightInfo passed from board click, OR
  // 2. It has intel_suite (is Vision-enriched from any board)
  const isHighlightedProp = useCallback((prop) => {
    // Check if prop has Vision Intel (from any board)
    const hasVisionIntel = prop.intel_suite && prop.is_vision_enriched;
    
    // If highlightInfo is provided, check for exact match
    if (highlightInfo) {
      // Use stat_type_extracted directly (PTS, REB, AST, etc.)
      const propStatType = normalizeStatType(prop.stat_type_extracted || prop.stat_type || '');
      const highlightStatType = normalizeStatType(highlightInfo.statType || '');
      
      const propDirection = (prop.direction || 'over').toLowerCase();
      const highlightDirection = (highlightInfo.direction || 'over').toLowerCase();
      
      const statMatch = propStatType.toUpperCase() === highlightStatType.toUpperCase();
      const lineMatch = Math.abs((prop.line || 0) - highlightInfo.line) < 0.1;
      const directionMatch = propDirection === highlightDirection;
      
      return statMatch && lineMatch && directionMatch;
    }
    
    // If no highlightInfo but prop has Vision Intel, highlight it
    return hasVisionIntel;
  }, [highlightInfo]);
  
  // Count demons/goblins
  const demons = player?.props?.filter(p => p.is_demon) || [];
  const goblins = player?.props?.filter(p => p.is_goblin) || [];
  const totalProps = player?.props?.length || 0;
  
  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 px-4 py-3">
        <div className="flex items-center gap-3 sm:gap-4">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onBack} 
            className="text-zinc-400 hover:text-white p-1 shrink-0" 
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          {player && (
            <PlayerHeadshot 
              playerName={playerName}
              team={player.team}
              sport={player.sport}
              teamLogoUrl={player.team_logo_url}
              photoUrl={player.photo_url}
              size="lg"
              className="shrink-0 sm:w-24 sm:h-24"
            />
          )}
          
          <div className="flex-1 min-w-0 overflow-hidden">
            <h1 className="text-base sm:text-2xl font-bold text-white leading-snug">{playerName}</h1>
            {player && (
              <div className="flex items-center gap-2 text-xs sm:text-sm text-zinc-400 mt-0.5">
                <span className="font-mono">{player.team}</span>
                {player.position && <span>· {player.position}</span>}
              </div>
            )}
          </div>
          
          {/* Demon/Goblin count badges - hidden on mobile */}
          {player && (
            <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
              {demons.length > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 rounded bg-red-500/20 border border-red-500/30">
                  <DemonIcon size={14} />
                  <span className="text-red-400 font-bold text-sm">{demons.length}</span>
                </div>
              )}
              {goblins.length > 0 && (
                <div className="flex items-center gap-1 px-2 py-1 rounded bg-green-500/20 border border-green-500/30">
                  <GoblinIcon size={14} />
                  <span className="text-green-400 font-bold text-sm">{goblins.length}</span>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Player Season Stats - Below header for cleaner mobile layout */}
        {player?.baseline_stats && (
          <div className="grid grid-cols-5 gap-2 mt-3 px-2 sm:px-4 pb-2 sm:flex sm:items-center sm:justify-center sm:gap-6">
            {player.baseline_stats.PTS?.season_avg != null && (
              <div className="text-center bg-zinc-800/50 rounded-lg py-2 sm:py-0 sm:bg-transparent">
                <div className="text-xl sm:text-lg font-bold text-white">{player.baseline_stats.PTS.season_avg.toFixed(1)}</div>
                <div className="text-[10px] sm:text-[10px] text-zinc-500 font-medium">PPG</div>
              </div>
            )}
            {player.baseline_stats.REB?.season_avg != null && (
              <div className="text-center bg-zinc-800/50 rounded-lg py-2 sm:py-0 sm:bg-transparent">
                <div className="text-xl sm:text-lg font-bold text-white">{player.baseline_stats.REB.season_avg.toFixed(1)}</div>
                <div className="text-[10px] sm:text-[10px] text-zinc-500 font-medium">RPG</div>
              </div>
            )}
            {player.baseline_stats.AST?.season_avg != null && (
              <div className="text-center bg-zinc-800/50 rounded-lg py-2 sm:py-0 sm:bg-transparent">
                <div className="text-xl sm:text-lg font-bold text-white">{player.baseline_stats.AST.season_avg.toFixed(1)}</div>
                <div className="text-[10px] sm:text-[10px] text-zinc-500 font-medium">APG</div>
              </div>
            )}
            {player.baseline_stats.STL?.season_avg != null && (
              <div className="text-center bg-zinc-800/50 rounded-lg py-2 sm:py-0 sm:bg-transparent">
                <div className="text-xl sm:text-lg font-bold text-cyan-400">{player.baseline_stats.STL.season_avg.toFixed(1)}</div>
                <div className="text-[10px] sm:text-[10px] text-zinc-500 font-medium">STL</div>
              </div>
            )}
            {player.baseline_stats.BLK?.season_avg != null && (
              <div className="text-center bg-zinc-800/50 rounded-lg py-2 sm:py-0 sm:bg-transparent">
                <div className="text-xl sm:text-lg font-bold text-cyan-400">{player.baseline_stats.BLK.season_avg.toFixed(1)}</div>
                <div className="text-[10px] sm:text-[10px] text-zinc-500 font-medium">BLK</div>
              </div>
            )}
          </div>
        )}
        
        {/* Remove old badges and vision insight - moved stats to header */}
      </div>
      
      {/* Content - Flat list with headers */}
      <div className="p-4">
        {loading ? (
          <SkeletonPlayerDetail />
        ) : error ? (
          <div className="text-center py-8 text-zinc-400">
            <p>{error}</p>
            <Button variant="outline" size="sm" onClick={onBack} className="mt-4">
              Go Back
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Summary bar */}
            <div className="flex items-center justify-between pb-3 border-b border-zinc-800">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-zinc-500" />
                <span className="text-sm font-medium text-zinc-400">
                  {sortedCategories.length} categories · {totalProps} total lines
                </span>
              </div>
            </div>
            
            {/* Flat list grouped by category */}
            {sortedCategories.length > 0 ? (
              sortedCategories.map(category => {
                const categoryProps = groupedProps[category] || [];
                const hasDemon = categoryProps.some(p => p.is_demon);
                const hasGoblin = categoryProps.some(p => p.is_goblin);
                
                // MLB: Sort purely by line (ascending - smallest first, largest at bottom)
                // NBA: Sort by tier priority, then by line within tier
                const sortedProps = [...categoryProps].sort((a, b) => {
                  if (currentSport === 'mlb') {
                    // MLB: Pure numerical sort by line (ascending)
                    return (a.line || 0) - (b.line || 0);
                  }
                  
                  // NBA: Tier priority: DEMON (0) > STANDARD (1) > GOBLIN (2)
                  const getTierPriority = (p) => {
                    if (p.is_demon) return 0;
                    if (p.is_goblin) return 2;
                    return 1; // STANDARD
                  };
                  
                  const aPriority = getTierPriority(a);
                  const bPriority = getTierPriority(b);
                  
                  if (aPriority !== bPriority) return aPriority - bPriority;
                  
                  // Within same tier, sort by line (desc for DEMON, asc for GOBLIN)
                  if (aPriority === 0) return (b.line || 0) - (a.line || 0); // DEMON: highest first
                  if (aPriority === 2) return (a.line || 0) - (b.line || 0); // GOBLIN: lowest first
                  return 0; // STANDARD: keep as is
                });
                
                return (
                  <div key={category}>
                    <CategoryHeader 
                      category={category}
                      count={categoryProps.length}
                      hasDemon={hasDemon}
                      hasGoblin={hasGoblin}
                    />
                    <div className="space-y-1.5 mt-2">
                      {sortedProps.map((prop, idx) => (
                        <PropRow
                          key={`${prop.market}-${prop.line}-${prop.direction}-${idx}`}
                          prop={prop}
                          isHighlighted={isHighlightedProp(prop)}
                          highlightRef={highlightRef}
                          onVisionClick={handleVisionClick}
                          gameLogs={prop.game_logs || player?.game_logs || []}
                          baselineStats={player?.baseline_stats || {}}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-lg">No available Bets today</p>
                <p className="text-sm mt-1">Check back closer to game time</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Vision Intel Suite Modal */}
      {showIntelSuite && selectedVisionProp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-zinc-900 border-2 border-amber-500/50 rounded-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto shadow-[0_0_50px_rgba(251,191,36,0.3)]">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-amber-950 via-yellow-900/50 to-amber-950 px-6 py-4 border-b border-amber-500/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Crosshair className="w-8 h-8 text-amber-400 animate-pulse" />
                  <div>
                    <h2 className="text-xl font-black text-amber-300">VISION INTEL SUITE</h2>
                    <p className="text-xs text-amber-400/70">{playerName} • {getPropLabel(selectedVisionProp.stat_type_extracted || selectedVisionProp.stat_type)}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowIntelSuite(false)}
                  className="p-2 hover:bg-amber-500/20 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5 text-amber-400" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Vision Pick Summary */}
              <div className="bg-gradient-to-r from-amber-950/50 to-zinc-900 border border-amber-500/30 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                    <Crosshair className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <div className="text-2xl font-black text-amber-300">
                      {(selectedVisionProp.direction || 'OVER').toUpperCase()} {selectedVisionProp.line}
                    </div>
                    <div className="text-sm text-amber-400/70">
                      {getPropLabel(selectedVisionProp.stat_type_extracted || selectedVisionProp.stat_type)}
                    </div>
                  </div>
                </div>
                
                {/* Stats Grid — baseline AVG values resolve via:
                    1. per-prop `l5_avg/l10_avg/season_avg`
                       (ferrari payload shape)
                    2. `player.baseline_stats[stat_type]` (master-hub
                       secondary fetch — populated for NBA tier picks
                       on this page)
                    so the L5/L10/SEASON cells are never blank for an
                    NBA prop the player actually has logs for. */}
                {(() => {
                  const _b =
                    player?.baseline_stats?.[selectedVisionProp.stat_type_extracted] ||
                    player?.baseline_stats?.[selectedVisionProp.stat_type] ||
                    {};
                  const l5  = selectedVisionProp.l5_avg     ?? _b.l5_avg     ?? null;
                  const l10 = selectedVisionProp.l10_avg    ?? _b.l10_avg    ?? null;
                  const ssn = selectedVisionProp.season_avg ?? _b.season_avg ?? null;
                  const fmt = (n) =>
                    n == null ? '-' : (typeof n === 'number' ? n.toFixed(1) : n);
                  return (
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t border-amber-500/20">
                      <div className="text-center">
                        <div className="text-xs text-amber-400/50">L5 AVG</div>
                        <div className="text-xl font-bold text-white" data-testid="vision-suite-l5-avg">
                          {fmt(l5)}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-amber-400/50">L10 AVG</div>
                        <div className="text-xl font-bold text-white" data-testid="vision-suite-l10-avg">
                          {fmt(l10)}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-amber-400/50">SEASON AVG</div>
                        <div className="text-xl font-bold text-white" data-testid="vision-suite-season-avg">
                          {fmt(ssn)}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
              
              {/* Add Pick to Command Center Button */}
              {onQuickAdd && (
                <button
                  onClick={() => {
                    onQuickAdd({
                      ...player,
                      ...selectedVisionProp,
                      player_name: playerName,
                      selectedProp: selectedVisionProp
                    });
                    setShowIntelSuite(false);
                  }}
                  className="w-full py-3 px-4 rounded-lg bg-cyan-500/20 border border-cyan-500/40 text-cyan-400 hover:bg-cyan-500/30 transition-all flex items-center justify-center gap-2 font-semibold"
                  data-testid="add-to-command-center-btn"
                >
                  <Plus className="w-5 h-5" />
                  Add Pick to Command Center
                </button>
              )}
              
              {/* ===== TEAM SIGNALS — backend-built deterministic team badges =====
                  When this modal is opened for a team prop, the player
                  Environmental Factors + Performance Indicators player
                  badges (Injured / Legal Noise / Hot Streak / etc.) are
                  irrelevant. Instead we render the team-specific
                  scout_badges the backend already supplies (Crown Play,
                  Brick Wall, Green Wave, Wolf Pack, Barrel Club, Icebox,
                  Freight Train, Fortress, Jet Fuel, etc. — see
                  `services/team_prop_tier_service._build_team_scout_badges`).
                  Each badge already carries label / reason / metric /
                  value / threshold / icon / color, so we render them
                  directly without going through BADGE_REGISTRY. */}
              {selectedVisionProp.is_team_prop && (() => {
                const teamBadges =
                  selectedVisionProp.scout_badges ||
                  selectedVisionProp.intel_suite?.scout_badges ||
                  [];
                const colorPalette = {
                  gold:   { bg: 'bg-amber-500/20',  border: 'border-amber-500/40',  text: 'text-amber-300',  glow: 'shadow-amber-500/30'  },
                  amber:  { bg: 'bg-amber-500/20',  border: 'border-amber-500/40',  text: 'text-amber-300',  glow: 'shadow-amber-500/30'  },
                  red:    { bg: 'bg-red-500/20',    border: 'border-red-500/40',    text: 'text-red-300',    glow: 'shadow-red-500/30'    },
                  green:  { bg: 'bg-green-500/20',  border: 'border-green-500/40',  text: 'text-green-300',  glow: 'shadow-green-500/30'  },
                  blue:   { bg: 'bg-blue-500/20',   border: 'border-blue-500/40',   text: 'text-blue-300',   glow: 'shadow-blue-500/30'   },
                  cyan:   { bg: 'bg-cyan-500/20',   border: 'border-cyan-500/40',   text: 'text-cyan-300',   glow: 'shadow-cyan-500/30'   },
                  purple: { bg: 'bg-purple-500/20', border: 'border-purple-500/40', text: 'text-purple-300', glow: 'shadow-purple-500/30' },
                  indigo: { bg: 'bg-indigo-500/20', border: 'border-indigo-500/40', text: 'text-indigo-300', glow: 'shadow-indigo-500/30' },
                  yellow: { bg: 'bg-yellow-500/20', border: 'border-yellow-500/40', text: 'text-yellow-300', glow: 'shadow-yellow-500/30' },
                  zinc:   { bg: 'bg-zinc-500/20',   border: 'border-zinc-500/40',   text: 'text-zinc-300',   glow: 'shadow-zinc-500/30'   },
                };
                return (
                  <div
                    className="bg-gradient-to-r from-zinc-900 to-zinc-800/50 border border-zinc-700 rounded-lg p-4"
                    data-testid="team-signals-section"
                  >
                    <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4 text-amber-400" />
                      TEAM SIGNALS
                    </h3>
                    <p className="text-xs text-zinc-500 mb-4">
                      Deterministic indicators built from this team&rsquo;s
                      historical outcomes, league ranks, and recent form
                    </p>
                    {teamBadges.length === 0 ? (
                      <div className="text-xs text-zinc-500 italic py-2">
                        No team signals fired for this prop.
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-3">
                        {teamBadges.map((b, idx) => {
                          const palette = colorPalette[b.color] || colorPalette.amber;
                          return (
                            <div
                              key={b.badge_key || b.label || idx}
                              className={`flex items-start gap-2 p-2 rounded-lg border ${palette.bg} ${palette.border} shadow-lg ${palette.glow}`}
                              data-testid={`team-signal-${b.badge_key || idx}`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${palette.bg} flex-shrink-0`}>
                                <Target size={16} className={palette.text} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className={`text-xs font-bold ${palette.text}`}>
                                  {b.label || b.name}
                                </div>
                                <div className="text-[10px] text-white leading-tight mt-0.5">
                                  {b.reason || b.description}
                                </div>
                                {b.metric && (b.value !== undefined && b.value !== null) && (
                                  <div className="text-[9px] font-mono text-zinc-400 mt-1">
                                    {b.metric}: <span className="text-zinc-200">{String(b.value)}</span>
                                    {b.threshold !== undefined && b.threshold !== null && (
                                      <span className="text-zinc-500"> · thr {String(b.threshold)}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className={`w-2 h-2 rounded-full ${palette.bg.replace('/20', '')} animate-pulse mt-1`} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* ===== CONTEXT BADGES - Sport-Specific Indicators (PLAYER ONLY) ===== */}
              {!selectedVisionProp.is_team_prop && (
              <div className="bg-gradient-to-r from-zinc-900 to-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-amber-400" />
                  ENVIRONMENTAL FACTORS
                </h3>
                <p className="text-xs text-zinc-500 mb-4">
                  Situational factors affecting tonight's performance
                </p>
                
                {/* Badge Grid - Filter by sport */}
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(BADGE_REGISTRY)
                    .filter(([_, badge]) => (!badge.sport || badge.sport === currentSport) && badge.category !== 'scout')
                    .map(([badgeKey, badge]) => {
                    // Current SoT for context badges on NBA Ferrari picks is
                    // ROOT-level `context_badges` (objects with badge_key).
                    // `intel_suite.context_badges` is a legacy/MLB array of
                    // strings — we keep reading it for MLB compatibility.
                    const rootContextBadges = selectedVisionProp.context_badges || [];
                    const suiteContextBadges = selectedVisionProp.intel_suite?.context_badges || [];
                    const allContextBadges = [...rootContextBadges, ...suiteContextBadges];
                    const isActive = selectedVisionProp.active_badges?.includes(badgeKey) || 
                                     allContextBadges.includes(badgeKey) ||
                                     allContextBadges.some(b => typeof b === 'object' && b.badge_key === badgeKey) ||
                                     selectedVisionProp.scout_badges?.some(b => b.id === badgeKey || b.badge_key === badgeKey);
                    
                    // Get custom description from the matching context badge
                    // (NBA Ferrari supplies a human-readable `description`).
                    const ctxBadgeMatch = allContextBadges.find(
                      (b) => typeof b === 'object' && b.badge_key === badgeKey
                    );
                    const playerBadge = player?.badges?.find(b => b.badge_key === badgeKey);
                    const mlbBadge = selectedVisionProp.scout_badges?.find(b => b.id === badgeKey);
                    const customDescription = isActive
                      ? (ctxBadgeMatch?.description || playerBadge?.description || mlbBadge?.name)
                      : null;
                    
                    return (
                      <BadgeGridItem 
                        key={badgeKey}
                        badgeKey={badgeKey}
                        isActive={isActive}
                        customDescription={customDescription}
                      />
                    );
                  })}
                </div>
                
                {/* Active Badges Summary */}
                {(() => {
                  const rootContextBadges = selectedVisionProp.context_badges || [];
                  const suiteContextBadges = selectedVisionProp.intel_suite?.context_badges || [];
                  const activeBadges = selectedVisionProp.active_badges || [];
                  const combined = [...activeBadges, ...rootContextBadges, ...suiteContextBadges];
                  if (combined.length === 0) return null;
                  return (
                    <div className="mt-4 pt-4 border-t border-zinc-700">
                      <div className="text-xs text-amber-400 font-semibold mb-2">
                        ACTIVE FOR {playerName?.toUpperCase()}:
                      </div>
                      <BadgeRow
                        badges={combined.map((b) => ({
                          badge_key: typeof b === 'string' ? b : b.badge_key,
                        }))}
                        size="md"
                      />
                    </div>
                  );
                })()}
              </div>
              )}
              
              {/* ===== SCOUT BADGES - AI & Model-Driven Indicators (PLAYER ONLY) ===== */}
              {!selectedVisionProp.is_team_prop && (() => {
                const scoutBadges = selectedVisionProp.scout_badges || selectedVisionProp.intel_suite?.scout_badges || [];
                const SCOUT_KEYS = ['hot_streak', 'floor_lock', 'lasso_high_edge', 'high_fidelity_model', 'soft_matchup', 'usage_spike', 'volatility_extreme'];
                const activeBadgeKeys = scoutBadges.map(b => typeof b === 'string' ? b : b.badge_key || b.id || '');
                
                return (
                  <div className="bg-gradient-to-r from-zinc-900 to-zinc-800/50 border border-zinc-700 rounded-lg p-4" data-testid="scout-badges-section">
                    <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-emerald-400" />
                      PERFORMANCE INDICATORS
                    </h3>
                    <p className="text-xs text-zinc-500 mb-4">
                      AI and model-driven signals based on projections, streaks, and matchup analysis
                    </p>
                    
                    <div className="grid grid-cols-2 gap-3">
                      {SCOUT_KEYS.filter(key => BADGE_REGISTRY[key]).map(badgeKey => {
                        const isActive = activeBadgeKeys.includes(badgeKey);
                        const matchingBadge = scoutBadges.find(b => 
                          (typeof b === 'string' ? b : b.badge_key || b.id) === badgeKey
                        );
                        const customDescription = isActive && typeof matchingBadge === 'object' ? matchingBadge.name || matchingBadge.description : null;
                        
                        return (
                          <BadgeGridItem
                            key={badgeKey}
                            badgeKey={badgeKey}
                            isActive={isActive}
                            customDescription={customDescription}
                          />
                        );
                      })}
                    </div>
                    
                    {activeBadgeKeys.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-zinc-700">
                        <div className="text-xs text-emerald-400 font-semibold mb-2">
                          ACTIVE FOR {playerName?.toUpperCase()}:
                        </div>
                        <BadgeRow 
                          badges={scoutBadges.map(b => ({
                            badge_key: typeof b === 'string' ? b : b.badge_key || b.id
                          }))}
                          size="md"
                        />
                      </div>
                    )}
                  </div>
                );
              })()}
              
              {/* ===== INTEL SUITE ADVANCED METRICS ===== */}
              {selectedVisionProp.intel_suite && (
                <div className="space-y-4">
                  {/* Usage Ripple (Operational Volume) */}
                  <div className="bg-gradient-to-r from-purple-950/40 to-zinc-900 border border-purple-500/30 rounded-lg p-4">
                    <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-purple-400" />
                      OPERATIONAL VOLUME
                    </h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-purple-300">
                          {selectedVisionProp.intel_suite.usage_ripple?.display || 'Standard Volume'}
                        </div>
                        <div className="text-xs text-zinc-400 mt-1">
                          {selectedVisionProp.intel_suite.usage_ripple?.reasoning}
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                        selectedVisionProp.intel_suite.usage_ripple?.bump_percent >= 5 
                          ? 'bg-purple-500 text-white' 
                          : selectedVisionProp.intel_suite.usage_ripple?.bump_percent >= 2
                            ? 'bg-purple-500/50 text-purple-200'
                            : 'bg-zinc-700 text-zinc-400'
                      }`}>
                        {selectedVisionProp.intel_suite.usage_ripple?.shift_label}
                      </div>
                    </div>
                    {selectedVisionProp.intel_suite.usage_ripple?.injuries_affecting?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-purple-500/20">
                        <div className="text-xs text-zinc-500 mb-1">LINEUP IMPACT:</div>
                        {selectedVisionProp.intel_suite.usage_ripple.injuries_affecting.map((inj, i) => (
                          <div key={i} className="text-xs text-purple-300">
                            • {inj.player} ({inj.status}) - {inj.injury}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Matchup Analysis - Sport-specific primary, shared fallback */}
                  {/* NBA: Momentum Tracker — CURRENT SOURCE OF TRUTH for
                      defensive momentum. Reads `momentum_data` (scoring
                      output emitted by Ferrari tier enrichment). */}
                  {currentSport === 'nba' && selectedVisionProp.momentum_data && (
                    <MomentumTrackerFull
                      momentumData={selectedVisionProp.momentum_data}
                      opponent={selectedVisionProp.intel_suite?.matchup_dvp?.opponent || selectedVisionProp.opponent || selectedVisionProp.opponent_abbr}
                      statType={selectedVisionProp.stat_type}
                    />
                  )}
                  
                  {/* MLB: Split Matchup Analysis */}
                  {currentSport === 'mlb' && (
                    <MLBMatchupAnalysis
                      matchupData={selectedVisionProp.matchup_analysis}
                      statType={selectedVisionProp.stat_type}
                      opponent={selectedVisionProp.opponent || selectedVisionProp.opponent_abbr}
                    />
                  )}
                  
                  {/* Fallback removed (2026-04-24, user directive Option B):
                      When `momentum_data` is missing, render NOTHING in
                      the Defensive Momentum slot. The previous
                      `intel_suite.matchup_dvp` block is intentionally
                      not rendered here — it is not Defensive Momentum,
                      and showing it as a substitute is misleading.
                      
                      Strict rule:
                        - momentum_data present → MomentumTrackerFull
                        - momentum_data missing → render nothing
                      
                      `intel_suite.matchup_dvp` continues to be available
                      to other sections (Tempo / Variance / etc.) but is
                      not rendered as a Defensive Momentum substitute. */}
                  
                  {/* Pace Delta (Tempo Multiplier) + Stability Index */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    {/* Tempo - MLB uses total_pct, NBA uses possessions */}
                    <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-2 sm:p-4 overflow-hidden">
                      <h3 className="text-[10px] sm:text-xs font-bold text-zinc-400 mb-1 sm:mb-2 truncate">TEMPO</h3>
                      {(() => {
                        const tempo = selectedVisionProp.intel_suite?.pace_delta || selectedVisionProp.intel_suite?.tempo;
                        const pct = tempo?.total_pct ?? tempo?.possessions ?? 0;
                        return (
                          <>
                            <div className={`text-lg sm:text-2xl font-bold truncate ${
                              pct >= 5 ? 'text-green-400' : pct <= -5 ? 'text-red-400' : 'text-white'
                            }`}>
                              {tempo?.display || '-'}
                            </div>
                            <div className="text-[9px] sm:text-xs text-zinc-500 mt-1 truncate">
                              {tempo?.tempo_label}
                            </div>
                            {tempo?.factors?.length > 0 && (
                              <div className="text-[8px] sm:text-[10px] text-zinc-600 mt-1 sm:mt-2 truncate hidden sm:block">
                                {tempo.factors.map(f => f.name).join(' + ')}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    
                    {/* Variance — uses the directly-computed CV /
                        volatility_score off the pick (always populated
                        by scoring) instead of `stability_index` which
                        produced std_dev=0 / "Unknown" for composite
                        MLB stat types like H+R+RBI because its
                        log-extractor doesn't unpack composites.
                        2026-05-02. */}
                    {(() => {
                      const cv = selectedVisionProp.cv ?? selectedVisionProp.cv_raw;
                      const volScore = selectedVisionProp.volatility_score;
                      const volLabel = selectedVisionProp.volatility_label;
                      // Color: low volatility = green, mid = yellow, high = red.
                      // volatility_score is 0-100 where HIGHER means MORE volatile,
                      // so we invert for the color palette.
                      let color = "text-zinc-300";
                      if (typeof volScore === "number") {
                        color = volScore <= 40
                          ? "text-green-400"
                          : volScore <= 70
                            ? "text-yellow-400"
                            : "text-red-400";
                      }
                      const display =
                        typeof volScore === "number"
                          ? `${Math.round(volScore)}/100`
                          : (typeof cv === "number" ? cv.toFixed(2) : "—");
                      const labelText = volLabel
                        ? `${volLabel.charAt(0).toUpperCase() + volLabel.slice(1)} volatility`
                        : (typeof cv === "number" ? "CV-based" : "No data");
                      return (
                        <div
                          className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-2 sm:p-4 overflow-hidden"
                          data-testid="variance-tile"
                        >
                          <h3 className="text-[10px] sm:text-xs font-bold text-zinc-400 mb-1 sm:mb-2 truncate">VARIANCE</h3>
                          <div
                            className={`text-lg sm:text-2xl font-bold truncate ${color}`}
                            data-testid="variance-value"
                          >
                            {display}
                          </div>
                          <div className="text-[9px] sm:text-xs text-zinc-500 mt-1 truncate">
                            {labelText}
                          </div>
                          {typeof cv === "number" && (
                            <div className="text-[8px] sm:text-[10px] text-zinc-600 mt-1 sm:mt-2 truncate hidden sm:block">
                              CV: {cv.toFixed(2)}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  
                  {/* Blowout Risk Warning */}
                  {selectedVisionProp.intel_suite?.blowout_risk?.risk_level && 
                   selectedVisionProp.intel_suite.blowout_risk.risk_level !== 'NONE' &&
                   selectedVisionProp.intel_suite.blowout_risk.risk_level !== 'UNKNOWN' && (
                    <div className={`border rounded-lg p-4 ${
                      selectedVisionProp.intel_suite.blowout_risk.risk_level === 'HIGH'
                        ? 'bg-gradient-to-r from-red-950/50 to-zinc-900 border-red-500/50 blowout-pulse-high'
                        : selectedVisionProp.intel_suite.blowout_risk.risk_level === 'MEDIUM'
                          ? 'bg-gradient-to-r from-orange-950/50 to-zinc-900 border-orange-500/40 blowout-pulse-medium'
                          : 'bg-zinc-800/50 border-zinc-700'
                    }`} data-testid="blowout-risk-warning">
                      <h3 className={`text-sm font-bold mb-2 flex items-center gap-2 ${
                        selectedVisionProp.intel_suite.blowout_risk.risk_level === 'HIGH'
                          ? 'text-red-400'
                          : selectedVisionProp.intel_suite.blowout_risk.risk_level === 'MEDIUM'
                            ? 'text-orange-400'
                            : 'text-zinc-400'
                      }`}>
                        {selectedVisionProp.intel_suite.blowout_risk.risk_level === 'HIGH' ? '⚠️' : '⚡'} BLOWOUT RISK
                      </h3>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm text-white">
                          {selectedVisionProp.intel_suite.blowout_risk.player_team_record} vs {selectedVisionProp.intel_suite.blowout_risk.opponent_team_record}
                        </div>
                        <div className={`px-2 py-1 rounded text-xs font-bold ${
                          selectedVisionProp.intel_suite.blowout_risk.risk_level === 'HIGH'
                            ? 'bg-red-500 text-white'
                            : selectedVisionProp.intel_suite.blowout_risk.risk_level === 'MEDIUM'
                              ? 'bg-orange-500 text-white'
                              : 'bg-zinc-600 text-white'
                        }`}>
                          {selectedVisionProp.intel_suite.blowout_risk.risk_level} RISK
                        </div>
                      </div>
                      <div className="text-xs text-zinc-400">
                        {selectedVisionProp.intel_suite.blowout_risk.risk_reason}
                      </div>
                      {selectedVisionProp.intel_suite.blowout_risk.warning && (
                        <div className={`mt-2 text-xs font-medium ${
                          selectedVisionProp.intel_suite.blowout_risk.risk_level === 'HIGH'
                            ? 'text-red-400'
                            : 'text-orange-400'
                        }`}>
                          {selectedVisionProp.intel_suite.blowout_risk.warning}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Officiating Impact (Whistle Matrix) */}
                  {selectedVisionProp.crew_chief && (
                    <div className={`border rounded-lg p-4 ${
                      selectedVisionProp.whistle_class === 'high_whistle'
                        ? 'bg-gradient-to-r from-amber-950/50 to-zinc-900 border-amber-500/40'
                        : selectedVisionProp.whistle_class === 'low_whistle'
                          ? 'bg-gradient-to-r from-blue-950/50 to-zinc-900 border-blue-500/40'
                          : 'bg-zinc-800/50 border-zinc-700'
                    }`} data-testid="officiating-impact-section">
                      <h3 className={`text-sm font-bold mb-2 flex items-center gap-2 ${
                        selectedVisionProp.whistle_class === 'high_whistle'
                          ? 'text-amber-400'
                          : selectedVisionProp.whistle_class === 'low_whistle'
                            ? 'text-blue-400'
                            : 'text-zinc-400'
                      }`}>
                        {selectedVisionProp.whistle_class === 'high_whistle' ? (
                          <GoldWhistleIcon className="w-4 h-4" />
                        ) : selectedVisionProp.whistle_class === 'low_whistle' ? (
                          <Snowflake className="w-4 h-4 text-blue-400" />
                        ) : (
                          <Scale className="w-4 h-4" />
                        )}
                        OFFICIATING IMPACT
                      </h3>
                      
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-lg font-bold text-white">
                            {selectedVisionProp.crew_chief}
                          </div>
                          <div className="text-xs text-zinc-400 mt-0.5">
                            {selectedVisionProp.ref_ou_pct != null && `${selectedVisionProp.ref_ou_pct}% Over`}
                            {selectedVisionProp.ref_ppg != null && ` | ${selectedVisionProp.ref_ppg} PPG`}
                          </div>
                        </div>
                        {selectedVisionProp.has_whistle_modifier && (
                          <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                            selectedVisionProp.whistle_class === 'high_whistle'
                              ? 'bg-amber-500 text-white'
                              : selectedVisionProp.whistle_class === 'low_whistle'
                                ? 'bg-blue-500 text-white'
                                : 'bg-zinc-600 text-white'
                          }`}>
                            {selectedVisionProp.whistle_class === 'high_whistle' ? 'HIGH WHISTLE' : 
                             selectedVisionProp.whistle_class === 'low_whistle' ? 'LOW WHISTLE' : 'NEUTRAL'}
                          </div>
                        )}
                      </div>
                      
                      {/* Point Lift Translation */}
                      {selectedVisionProp.lift_label && selectedVisionProp.lift_type !== 'neutral' && (
                        <div className={`bg-zinc-800/50 rounded-lg p-3 ${
                          selectedVisionProp.whistle_class === 'high_whistle' ? 'border border-amber-500/20' :
                          selectedVisionProp.whistle_class === 'low_whistle' ? 'border border-blue-500/20' : ''
                        }`}>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-zinc-500">POINT LIFT</span>
                            <span className={`text-sm font-bold ${
                              selectedVisionProp.lift_type === 'boost' ? 'text-amber-400' : 'text-blue-400'
                            }`}>
                              {selectedVisionProp.lift_label}
                            </span>
                          </div>
                          <div className="text-xs text-zinc-400 mt-2">
                            {selectedVisionProp.whistle_class === 'high_whistle' ? (
                              <>This crew historically boosts scoring. Games average <span className="text-amber-400">{selectedVisionProp.ref_ppg} PPG</span> with a <span className="text-amber-400">{selectedVisionProp.ref_ou_pct}%</span> over rate.</>
                            ) : selectedVisionProp.whistle_class === 'low_whistle' ? (
                              <>This crew suppresses scoring. Games average only <span className="text-blue-400">{selectedVisionProp.ref_ppg} PPG</span> with just a <span className="text-blue-400">{selectedVisionProp.ref_ou_pct}%</span> over rate.</>
                            ) : null}
                          </div>
                          {selectedVisionProp.foul_rate_diff !== 0 && (
                            <div className="text-xs text-zinc-500 mt-1">
                              Foul rate vs league avg: <span className={selectedVisionProp.foul_rate_diff > 0 ? 'text-amber-400' : 'text-blue-400'}>
                                {selectedVisionProp.foul_rate_diff > 0 ? '+' : ''}{selectedVisionProp.foul_rate_diff}%
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Usage Vacuum Alert */}
                  {selectedVisionProp.has_vacuum_modifier && selectedVisionProp.vacuum_data && (
                    <div className="bg-gradient-to-r from-orange-950/50 to-zinc-900 border border-orange-500/40 rounded-lg p-4" data-testid="usage-vacuum-section">
                      <h3 className="text-sm font-bold text-orange-400 mb-2 flex items-center gap-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 6v6l4 2" />
                        </svg>
                        USAGE VACUUM ALERT
                        <span className="ml-auto text-[10px] font-normal text-orange-300/70">
                          {new Date(selectedVisionProp.vacuum_data.confirmed_at || selectedVisionProp.vacuum_data.triggered_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                      </h3>
                      
                      <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 mb-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-[10px] font-bold text-orange-400 bg-orange-500/20 px-2 py-0.5 rounded">INJURY ALERT</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                            selectedVisionProp.vacuum_data.beneficiary_rank === 'primary' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {selectedVisionProp.vacuum_data.beneficiary_rank === 'primary' ? 'PRIMARY' : 'SECONDARY'} BENEFICIARY
                          </span>
                        </div>
                        <p className="text-sm text-white leading-relaxed">
                          <span className="text-red-400 font-bold">{selectedVisionProp.vacuum_data.injured_player}</span> is <span className="text-red-400 font-bold">OUT</span>.
                          {' '}{selectedVisionProp.player_name || 'This player'} usage projected <span className="text-green-400 font-bold">+{selectedVisionProp.vacuum_data.usage_bump}%</span>.
                        </p>
                        {selectedVisionProp.vacuum_data.reason && (
                          <p className="text-xs text-zinc-400 mt-1">
                            Reason: {selectedVisionProp.vacuum_data.reason}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-zinc-400">
                          Team: {selectedVisionProp.vacuum_data.injured_team}
                        </div>
                        <div className="text-sm font-bold text-orange-400">
                          +{selectedVisionProp.vacuum_modifier} Power Boost
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Vision Intel (Target-Lock Rationale) - CLEANED: Direct read of vision_intel and vision_badge */}
                  {(() => {
                    const intelText = selectedVisionProp?.vision_intel;
                    const badgeText = selectedVisionProp?.vision_badge;
                    
                    if (!intelText) return null;
                    
                    return (
                      <div className="bg-gradient-to-r from-amber-950/50 to-zinc-900 border border-amber-500/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-bold text-amber-300 flex items-center gap-2">
                            <Crosshair className="w-4 h-4 text-amber-400" />
                            TARGET-LOCK RATIONALE
                          </h3>
                          {badgeText && (
                            <span className={`text-[10px] font-bold px-2 py-1 rounded ${
                              badgeText.toLowerCase().includes('safe') || badgeText.toLowerCase().includes('goblin')
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : badgeText.toLowerCase().includes('front')
                                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                  : 'bg-red-500/20 text-red-400 border border-red-500/30'
                            }`}>
                              {badgeText}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-white leading-relaxed">
                          {intelText}
                        </p>
                        <div className="mt-3 pt-2 border-t border-amber-500/20 flex items-center justify-between">
                          <span className="text-[10px] text-amber-400/70">Powered by Vision Intel</span>
                          {selectedVisionProp.board_score && (
                            <span className="text-[10px] text-zinc-400">
                              Board Score: <span className="text-white font-bold">{selectedVisionProp.board_score}</span>
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              
              {/* Hit Rate Analysis — 2026-05-01: L20 / L10 / L5 trio
                  matches the pick card byte-for-byte. L20 is the gate
                  input, L10 is graph parity, L5 is the recent-form
                  sub-gate input. */}
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-cyan-400" />
                  HIT RATE ANALYSIS
                </h3>
                {(() => {
                  const colorFor = (r) =>
                    r == null ? 'text-zinc-500'
                      : r >= 70 ? 'text-green-400'
                      : r >= 50 ? 'text-yellow-400'
                      : 'text-red-400';
                  // 2026-05-07 P0 Phase 4B: canonical-only reads.
                  // Backend no longer ships legacy `h10_rate`/
                  // `h5_rate`/`hit_rates.{l10,l5}.hit_rate` on tier
                  // picks; the L20/L10/L5 cells consume the
                  // score-doc SSOT directly.
                  const l20 = selectedVisionProp.hit_rate_l20 ?? null;
                  const l10 = selectedVisionProp.hit_rate_l10 ?? null;
                  const l5  = selectedVisionProp.hit_rate_l5  ?? null;
                  // 2026-05-11 — baseline-stats fallback so the
                  // "Avg: x.x" sub-labels below the HR cells aren't
                  // stranded when the per-prop l*_avg is missing
                  // (the player-with-badges payload doesn't ship it).
                  const _b =
                    player?.baseline_stats?.[selectedVisionProp.stat_type_extracted] ||
                    player?.baseline_stats?.[selectedVisionProp.stat_type] ||
                    {};
                  const subL10 = selectedVisionProp.l10_avg ?? _b.l10_avg ?? null;
                  const subL5  = selectedVisionProp.l5_avg  ?? _b.l5_avg  ?? null;
                  const fmtAvg = (n) =>
                    n == null ? null : `Avg: ${typeof n === 'number' ? n.toFixed(1) : n}`;
                  const Cell = ({ label, sub, rate, testid }) => (
                    <div className="bg-zinc-900/50 rounded-lg p-3" data-testid={testid}>
                      <div className="text-xs text-zinc-500">{label}</div>
                      <div className={`text-2xl font-bold ${colorFor(rate)}`}>
                        {rate != null ? `${Math.round(rate)}%` : '—'}
                      </div>
                      {sub && (
                        <div className="text-xs text-zinc-500 mt-1">{sub}</div>
                      )}
                    </div>
                  );
                  return (
                    <div className="grid grid-cols-3 gap-3">
                      <Cell
                        label="LAST 20 (GATE)"
                        rate={l20}
                        sub={selectedVisionProp.hit_rate_sample_size != null
                          ? `${selectedVisionProp.hit_rate_sample_size} games`
                          : null}
                        testid="player-detail-hr-l20"
                      />
                      <Cell
                        label="LAST 10"
                        rate={l10}
                        sub={fmtAvg(subL10)}
                        testid="player-detail-hr-l10"
                      />
                      <Cell
                        label="LAST 5"
                        rate={l5}
                        sub={fmtAvg(subL5)}
                        testid="player-detail-hr-l5"
                      />
                    </div>
                  );
                })()}
                
                {/* Additional MLB Stats Row.
                    SSOT Tier E (2026-05-04): derive edge % from
                    canonical `edge_vs_fair` ratio (× 100). Legacy
                    `edge_pct` reads removed.
                    2026-05-14 — Universal display contract:
                      • Consensus Edge = edge_vs_fair (model vs market)
                      • Best Bet Edge  = total_edge   (model vs best book)
                      • Best Bet       = best_book + best_book_odds */}
                {(() => {
                  const _consensusEdgePct = (selectedVisionProp.edge_vs_fair != null)
                    ? Number(selectedVisionProp.edge_vs_fair) * 100
                    : null;
                  const _bestBetEdgePct = (selectedVisionProp.total_edge != null)
                    ? Number(selectedVisionProp.total_edge) * 100
                    : null;
                  const _bestBetBook = selectedVisionProp.best_book;
                  const _bestBetOdds = selectedVisionProp.best_book_odds;
                  const _fmtOdds = (o) => o == null ? '' : (o > 0 ? `+${o}` : `${o}`);
                  const _fmtBook = (b) => {
                    if (!b) return '';
                    const map = {
                      'draftkings': 'DraftKings', 'fanduel': 'FanDuel',
                      'betmgm': 'BetMGM', 'caesars': 'Caesars',
                      'espnbet': 'ESPN BET', 'hardrockbet': 'Hard Rock',
                      'betrivers': 'BetRivers', 'betparx': 'BetPARX',
                      'ballybet': 'Bally Bet', 'fliff': 'Fliff',
                      'pointsbetus': 'PointsBet',
                    };
                    return map[b] || b;
                  };
                  const _show = (
                    selectedVisionProp.cv != null
                    || _consensusEdgePct != null
                    || _bestBetEdgePct != null
                    || _bestBetBook
                    || selectedVisionProp.tp_odds != null
                  );
                  const _bestBetLabel = _bestBetBook
                    ? `${_fmtBook(_bestBetBook)}${_bestBetOdds ? ' ' + _fmtOdds(_bestBetOdds) : ''}`
                    : null;
                  return _show && (
                  <div className="space-y-2 mt-3 pt-3 border-t border-zinc-700">
                    <div className="grid grid-cols-4 gap-2">
                      {selectedVisionProp.cv != null && (
                        <div className="text-center" data-testid="player-detail-cv">
                          <div className="text-[10px] text-zinc-500 uppercase">CV</div>
                          <div className={`text-sm font-bold ${selectedVisionProp.cv < 0.6 ? 'text-green-400' : selectedVisionProp.cv < 1.0 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {typeof selectedVisionProp.cv === 'number' ? selectedVisionProp.cv.toFixed(2) : selectedVisionProp.cv}
                          </div>
                        </div>
                      )}
                      {_consensusEdgePct != null && (
                        <div className="text-center" data-testid="player-detail-consensus-edge"
                             title="Consensus Edge compares our model to the overall market (devigged fair probability).">
                          <div className="text-[10px] text-zinc-500 uppercase">Consensus Edge</div>
                          <div className={`text-sm font-bold ${_consensusEdgePct > 15 ? 'text-green-400' : _consensusEdgePct > 5 ? 'text-yellow-400' : 'text-zinc-400'}`}>
                            {_consensusEdgePct > 0 ? '+' : ''}{_consensusEdgePct.toFixed(1)}%
                          </div>
                        </div>
                      )}
                      {_bestBetEdgePct != null && (
                        <div className="text-center" data-testid="player-detail-best-bet-edge"
                             title="Best Bet Edge compares our model to the best sportsbook price currently available — the actionable bettor edge.">
                          <div className="text-[10px] text-zinc-500 uppercase">Best Bet Edge</div>
                          <div className={`text-sm font-bold ${_bestBetEdgePct > 10 ? 'text-green-400' : _bestBetEdgePct > 3 ? 'text-yellow-400' : _bestBetEdgePct < 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                            {_bestBetEdgePct > 0 ? '+' : ''}{_bestBetEdgePct.toFixed(1)}%
                          </div>
                        </div>
                      )}
                      {selectedVisionProp.tp_odds != null && (
                        <div className="text-center" data-testid="player-detail-true-prob">
                          <div className="text-[10px] text-zinc-500 uppercase">True Prob</div>
                          <div className={`text-sm font-bold ${selectedVisionProp.tp_odds > 70 ? 'text-green-400' : selectedVisionProp.tp_odds > 55 ? 'text-yellow-400' : 'text-zinc-400'}`}>
                            {typeof selectedVisionProp.tp_odds === 'number' ? selectedVisionProp.tp_odds.toFixed(1) : selectedVisionProp.tp_odds}%
                          </div>
                        </div>
                      )}
                    </div>
                    {_bestBetLabel && (
                      <div className="flex items-center justify-between text-xs"
                           data-testid="player-detail-best-bet-row">
                        <span className="text-zinc-500 uppercase tracking-wide">Best Bet</span>
                        <span className="font-semibold text-emerald-300">{_bestBetLabel}</span>
                      </div>
                    )}
                  </div>
                  );
                })()}
              </div>
              
              {/* Vision Intel Summary - shown only if not already shown above */}
              {selectedVisionProp.vision_intel && !selectedVisionProp.intel_suite && (
                <div className="bg-gradient-to-r from-amber-950/50 to-zinc-900 border border-amber-500/30 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-amber-300 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4 text-amber-400" />
                    VISION INTEL
                  </h3>
                  <p className="text-sm text-zinc-200 leading-relaxed">
                    {selectedVisionProp.vision_intel}
                  </p>
                  {selectedVisionProp.vision_badge && (
                    <div className="mt-2 text-[10px] text-amber-400/70">
                      Badge: <span className="text-white font-medium">{selectedVisionProp.vision_badge}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* AI Analysis (if available) */}
              {player?.ai_vision && (
                <div className="bg-zinc-800/50 border border-cyan-500/30 rounded-lg p-4">
                  <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4 text-cyan-400" />
                    AI TACTICAL ANALYSIS
                  </h3>
                  <p className="text-sm text-zinc-300 leading-relaxed">
                    {player.ai_vision}
                  </p>
                </div>
              )}
              
              {/* Recommendation Badge */}
              <div className="flex justify-center">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-400 text-black rounded-full font-bold text-sm">
                  <Crosshair className="w-4 h-4" />
                  VISION RECOMMENDS: {(selectedVisionProp.direction || 'OVER').toUpperCase()} {selectedVisionProp.line}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayerDetailPage;
